// Copyright (c) 2024 embyt GmbH. All rights reserved.
// Author: Marc Planeck

import * as React from "react";
import { useMemo } from "react";
import { connect } from "react-redux";
import { defineMessages, useIntl } from "react-intl";

import { IState } from "states/states";
import { ActionType, IAction, IActionTypeSelectTariffCircuits, raiseAction } from "actions";
import { isSet } from "shared/utils/utils";

import TariffCircuitListModel from "model/tariffcircuitlist";

import TreeViewDropDown, {
  HierarchySelectionState,
  ITreeViewDropDownOption,
} from "./TreeViewDropDown";
import { IEnergyMeter, ITariffCircuit } from "shared/types/parameter";
import { checkCategories, getSiteSelection } from "guiutils";
import { Category } from "states/cachedstates";

import cssContent from "constants.module.scss";

// ITariffCircuitDropDownExtProps defines the external properties for the TariffCircuitTreeViewDropDown component
interface ITariffCircuitDropDownExtProps {
  // [optional] the orientation of the dropdown menu, can be "left" or "right"
  //    default is "left"
  menuOrientation?: "left" | "right";
  // [optional] additional CSS class name for styling the component
  className?: string;
}

interface ITariffCircuitDropDownProps {
  energySource?: string;
  tariffCircuits: ITariffCircuit[];
  energyMeters: IEnergyMeter[];
  selectedCategory: Category | undefined;
  selectedTariffIds?: number[];
  isHierarchyViewMode: boolean;
}

const mapStateToProps = (state: IState, ownProps: ITariffCircuitDropDownExtProps) => {
  const selection = getSiteSelection(state);
  const tariffCircuits = state.para.parameter?.tariffline ?? [];
  const defaultHierarchyViewMode = TariffCircuitListModel.hasHierarchy(tariffCircuits);

  const props: ITariffCircuitDropDownProps = {
    energySource: selection?.energySource,
    tariffCircuits: tariffCircuits,
    energyMeters: state.para.parameter?.energymeter ?? [],
    selectedCategory: selection?.category,
    selectedTariffIds: selection?.tariffCircuitIds,
    isHierarchyViewMode: selection?.isHierarchyViewMode ?? defaultHierarchyViewMode,
  };

  return props;
};

interface ITariffCircuitDropDownDispatch {
  onDropdownTCSelectionChanged: (
    selectedIds: number[] | undefined,
    dropDownHierarchyMode?: boolean,
  ) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ITariffCircuitDropDownDispatch = {
    onDropdownTCSelectionChanged: (
      selectedIds: number[] | undefined,
      isHierarchyViewMode?: boolean,
    ) => {
      const payload: IActionTypeSelectTariffCircuits = { selectedIds, isHierarchyViewMode };
      dispatch(raiseAction(ActionType.ACT_SELECT_TARIFFCIRCUITS, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  selectTariffCircuit: {
    id: "Select Tariff Circuit",
  },
  noTariffCircuits: {
    id: "No tariff circuits present.",
  },
  searchTariffCircuits: {
    id: "Search for Tariff Circuit",
  },
  noCategories: {
    id: "No categories",
  },
});

type TariffCircuitDropDownAllProps = ITariffCircuitDropDownProps &
  ITariffCircuitDropDownExtProps &
  ITariffCircuitDropDownDispatch;

const TariffCircuitDropDown: React.FC<TariffCircuitDropDownAllProps> = (props) => {
  const intl = useIntl();
  // Only re-runs when tariffs, or energy meters change.
  const tcModel = useMemo(
    () => new TariffCircuitListModel(props.tariffCircuits, props.energyMeters),
    [props.tariffCircuits, props.energyMeters],
  );

  // Expensive: rebuilds the tree structure. Only re-runs when tariffs, energy source or category change.
  const structure = useMemo(
    () => tcModel.makeOptionsArray(props.energySource, props.selectedCategory),
    [tcModel, props.energySource, props.selectedCategory],
  );

  // Cheap: clones the cached structure and stamps checked states. Re-runs on every selection change.
  const options = useMemo(() => {
    const opts = tcModel.applySelection(
      structure,
      props.selectedTariffIds,
      props.isHierarchyViewMode,
    );
    opts.forEach((item) => {
      item.color =
        (
          (props.isHierarchyViewMode &&
            (item.checked === HierarchySelectionState.SELECTED ||
              item.checked === HierarchySelectionState.SELECTED_CHILD)) ||
          checkCategories(props.selectedCategory, item.referenceEntry?.categories)
        ) ?
          item.color
        : cssContent.TREEVIEW_DISABLED_OPTION;
    });
    return opts;
  }, [
    tcModel,
    structure,
    props.selectedTariffIds,
    props.isHierarchyViewMode,
    props.selectedCategory,
  ]);

  // only re-runs when options change,
  // which is when tariffs, energy source, category or selection changes.
  // Checks if at least one option has children to decide whether to show the hierarchy mode toggle.
  const hasChildren = useMemo(
    () => options.some((item) => isSet(item.referenceEntry) && item.hasChildren),
    [options],
  );

  return (
    <TreeViewDropDown
      {...props}
      className={[props.className, "btn", "btn-secondary", "p-0"].filter(Boolean).join(" ")}
      placeholder={intl.formatMessage(messages.selectTariffCircuit)}
      placeholderNoOption={intl.formatMessage(messages.noTariffCircuits)}
      searchText={intl.formatMessage(messages.searchTariffCircuits)}
      options={options}
      menuOrientation={props.menuOrientation}
      onItemsChanged={(items: ITreeViewDropDownOption[]) => {
        props.onDropdownTCSelectionChanged(
          items
            .filter(
              (item) =>
                (item.checked === HierarchySelectionState.SELECTED ||
                  item.checked === HierarchySelectionState.SELECTED_CHILD) &&
                item.referenceEntry !== undefined,
            )
            .map((item) => item.entryId.at(-1) ?? -1),
        );
      }}
      isHierarchyMode={props.isHierarchyViewMode}
      onHierarchyModeChanged={
        hasChildren ?
          (isHierarchyMode: boolean) => {
            const newSelectedTariffs =
              isHierarchyMode === true ?
                tcModel.updateHierarchySelection(
                  options,
                  props.selectedTariffIds,
                  props.selectedCategory,
                )
              : tcModel.updateNormalSelection(options, props.selectedTariffIds);

            props.onDropdownTCSelectionChanged(newSelectedTariffs, isHierarchyMode);

            return newSelectedTariffs?.[0];
          }
        : undefined
      }
      renderToolTip={(item) =>
        (
          isSet(item.referenceEntry?.categories) &&
          item.referenceEntry.categories.length > 0 &&
          item.referenceEntry.categories[0] !== ""
        ) ?
          item.referenceEntry?.categories?.join(",")
        : intl.formatMessage(messages.noCategories)
      }
    />
  );
};

const DropdownSelect = connect(mapStateToProps, mapDispatchToProps)(TariffCircuitDropDown);

export default DropdownSelect;
